/**
 * Opening a request period: the rules the admin's form and the server share.
 *
 * - A new period starts on a Monday, ends after it, and is due before it
 *   starts (as before).
 * - Its due date is today or later: a period due yesterday would be born
 *   closed. This is the same rule reopening a period uses (reopen.ts).
 * - It may not share a single day with another period. Two periods over the
 *   same weeks would split a physician's requests between them, and the
 *   admin's review list would show only one half.
 * - Left unnamed, it is named after its dates, day first ("4 Jan – 4 Jul
 *   2027"), the same words the form shows as the name's placeholder.
 *
 * Pure, so the screen and the server say the same thing.
 */
import { addDays, compareDates, dayOfWeek, mondayOnOrBefore, MONDAY, type ISODate } from '../dates/iso.ts';
import { formatPeriod } from '../format/dates.ts';
import { reopenProblem } from './reopen.ts';

/** How long before a period starts its requests are due, by default. */
export const DEFAULT_DUE_DAYS_BEFORE = 28;

export interface PeriodSpan {
  startDate: ISODate;
  endDate: ISODate;
}

/**
 * Why a new period cannot be opened with these dates:
 * - `dates`: it does not start on a Monday, does not end after it starts,
 *   or is not due before it starts;
 * - `due-date-past`: the due date has already gone by (or the period has
 *   already started, which leaves no due date that could work).
 */
export type NewPeriodProblem = 'dates' | 'due-date-past';

export function newPeriodProblem(start: ISODate, end: ISODate, dueDate: ISODate, today: ISODate): NewPeriodProblem | null {
  if (dayOfWeek(start) !== MONDAY || compareDates(end, start) <= 0 || compareDates(dueDate, start) >= 0) return 'dates';
  // The reopening rule, word for word: from today up to the day before it starts.
  // A period that has already started fails it too, since its due date is then past.
  const problem = reopenProblem(start, dueDate, today);
  if (problem === 'started' || problem === 'due-date-past') return 'due-date-past';
  return null;
}

/** The first existing period that shares at least one day with start to end, or null. */
export function overlappingPeriod<T extends PeriodSpan>(periods: readonly T[], start: ISODate, end: ISODate): T | null {
  return periods.find((p) => compareDates(p.startDate, end) <= 0 && compareDates(start, p.endDate) <= 0) ?? null;
}

/**
 * Where the form's start date begins: the Monday after the latest existing
 * period ends, so pressing Open with the defaults never repeats a period
 * already open. The suggested schedule start (the Monday after the last
 * saved schedule) wins when it is later, and is used when there are none.
 */
export function defaultPeriodStart(periods: readonly PeriodSpan[], suggestedStart: ISODate | null): ISODate | null {
  let latestEnd: ISODate | null = null;
  for (const p of periods) if (latestEnd === null || compareDates(p.endDate, latestEnd) > 0) latestEnd = p.endDate;
  const afterLatest = latestEnd === null ? null : addDays(mondayOnOrBefore(latestEnd), 7);
  if (afterLatest === null) return suggestedStart;
  if (suggestedStart === null) return afterLatest;
  return compareDates(suggestedStart, afterLatest) > 0 ? suggestedStart : afterLatest;
}

/**
 * The due date the form offers: four weeks before the period starts, or
 * today if that has already gone by (still before the start). A period that
 * starts today or earlier has no due date that works; the four-week date is
 * offered and the server explains.
 */
export function defaultDueDate(start: ISODate, today: ISODate): ISODate {
  const fourWeeks = addDays(start, -DEFAULT_DUE_DAYS_BEFORE);
  if (compareDates(fourWeeks, today) >= 0) return fourWeeks;
  return compareDates(today, start) < 0 ? today : fourWeeks;
}

/** A period's name when the admin leaves it blank: its dates, day first, as the form's placeholder shows them. */
export function defaultPeriodName(start: ISODate, end: ISODate): string {
  return formatPeriod(start, end);
}
